import { makeAutoObservable } from "mobx";
import type { StoreDiagnosticEvent } from "@intrasync/stores";
import type RootStore from "./RootStore";
import { isAdmin } from "../auth/tier";
import {
  hqDiagnosticsBuffer,
  type RetainedDiagnostic,
} from "../diagnostics/diagnosticsBuffer";
import {
  formatDiagnostics,
  getBootLog,
  getBootLogDroppedCount,
  getSuppressedCount,
  isInfoCaptureEnabled,
  setInfoCapture,
} from "../diagnostics/diagnosticsConfig";

export type DiagnosticsFilter = "all" | "error" | "warn";

/**
 * View state for the admin diagnostics panel.
 *
 * The retained events themselves live in the module-scope
 * {@link hqDiagnosticsBuffer}, NOT here — the sink is configured in
 * `rootStoreConfig.ts`, which is evaluated before any store exists, and the
 * events fired inside the RootStore constructor (duplicate store keys, the
 * registry's first collision) are precisely the ones worth keeping. A store
 * that owned the buffer would start life after the interesting part was over.
 *
 * This store is the reactive facade: filter/search state, the derived list,
 * the counters, and the admin gate.
 */
class DiagnosticsStore {
  rootStore: RootStore;
  filter: DiagnosticsFilter = "all";
  search = "";
  /** Mirrors the module-level info-capture flag so the toggle re-renders. */
  infoCapture = isInfoCaptureEnabled();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // The three getters below read MODULE state (the boot log and the
    // suppressed-info counter), not observables — as computeds they would
    // cache their first value forever. Excluded so every read is fresh.
    makeAutoObservable(this, {
      rootStore: false,
      bootLog: false,
      bootLogText: false,
      bootLogDroppedCount: false,
      suppressedCount: false,
    });
  }

  /** The panel is admin-only: these events name internal stores and services. */
  get canView(): boolean {
    return isAdmin(this.rootStore);
  }

  setFilter = (filter: DiagnosticsFilter): void => {
    this.filter = filter;
  };

  setSearch = (search: string): void => {
    this.search = search;
  };

  /**
   * Re-open `info` capture at runtime. Off by default after boot: `info` is
   * high-volume and only interesting while stores are coming up, but an admin
   * chasing a null feature store needs to be able to turn it back on without a
   * rebuild.
   */
  setInfoCapture = (enabled: boolean): void => {
    this.infoCapture = enabled;
    setInfoCapture(enabled);
  };

  clear = (): void => {
    hqDiagnosticsBuffer.clear();
  };

  /** Retained warn/error events, newest first, after filter + search. */
  get events(): RetainedDiagnostic[] {
    const needle = this.search.trim().toLowerCase();
    return hqDiagnosticsBuffer.events
      .filter((event) => this.filter === "all" || event.level === this.filter)
      .filter((event) => {
        if (!needle) return true;
        return (
          event.store.toLowerCase().includes(needle) ||
          event.code.toLowerCase().includes(needle) ||
          event.message.toLowerCase().includes(needle)
        );
      })
      .reverse();
  }

  get errorCount(): number {
    return hqDiagnosticsBuffer.errorCount;
  }

  get warnCount(): number {
    return hqDiagnosticsBuffer.warnCount;
  }

  get infoCount(): number {
    return hqDiagnosticsBuffer.totals.info;
  }

  /** Events the ring evicted (capacity is fixed — see DiagnosticsBuffer). */
  get droppedCount(): number {
    return hqDiagnosticsBuffer.droppedCount;
  }

  /** `info`/`debug` events seen after capture was turned off. */
  get suppressedCount(): number {
    return getSuppressedCount();
  }

  /** Everything the sink delivered during the boot window, at every level. */
  get bootLog(): StoreDiagnosticEvent[] {
    return getBootLog();
  }

  get bootLogDroppedCount(): number {
    return getBootLogDroppedCount();
  }

  /** Plain-text dump for the panel's copy button / a bug report. */
  get retainedText(): string {
    return formatDiagnostics(hqDiagnosticsBuffer.events);
  }

  get bootLogText(): string {
    return formatDiagnostics(getBootLog());
  }
}

export default DiagnosticsStore;
